import { createPortal } from "react-dom";
import React, { useState, useMemo, useEffect } from "react";
import I from "../component/Icon";
import { EXAM_CATS, EXAM_TYPES } from "../data/mockdata";
import ExamForm from "../component/ExamForm";
import ExamDetailView from "../component/ExamDetailView";
import ConfirmDlg from "../component/ConfirmDialog";
import Bdg from "../component/Badge";
import SB from "../component/SearchBox";
import apiService from "../services/apiService";

const COURIER_FEE_PER_EXAM = 50;
const normalizeName = (value) => String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
const number = (value, fallback = 0) => {
  const result = Number(value);
  return Number.isFinite(result) ? result : fallback;
};
const money = (value) => `₹${number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const asBool = (value) => value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true";
const field = (obj, ...names) => {
  for (const name of names) {
    if (obj?.[name] !== undefined && obj?.[name] !== null) return obj[name];
  }
  return undefined;
};
const getDetails = (student) => {
  const rows = field(student, "stdDetlst", "StdDetlst", "studentDetails", "StudentDetails") ?? [];
  return Array.isArray(rows) ? rows : [];
};
const getOutput = (data) => field(data, "output", "Output", "data", "Data") ?? data;
const getList = (data) => {
  const output = getOutput(data);
  if (Array.isArray(output)) return output;
  return field(output, "result", "Result", "data", "Data", "items", "Items") ?? [];
};
const ensureSuccess = (data, fallback) => {
  if (data?.isSuccess === false || data?.IsSuccess === false) {
    throw new Error(field(data, "failureReason", "FailureReason", "message", "Message") || fallback);
  }
  return data;
};
const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data?.errors && typeof data.errors === "object") {
    const messages = Object.values(data.errors).flat().filter(Boolean).join(" | ");
    if (messages) return messages;
  }
  return field(data, "failureReason", "FailureReason", "message", "Message", "title", "Title", "error") || error?.message || fallback;
};
const showNotification = (type, message) => {
  if (typeof document === "undefined") return;
  document.getElementById("exams-page-notification")?.remove();
  const node = document.createElement("div");
  node.id = "exams-page-notification";
  node.textContent = `${type === "success" ? "✓" : "✕"} ${message}`;
  Object.assign(node.style, {
    position: "fixed", top: "20px", right: "20px", zIndex: "9999999", minWidth: "280px", maxWidth: "420px",
    padding: "12px 16px", borderRadius: "8px", background: type === "success" ? "#d1e7dd" : "#f8d7da",
    color: type === "success" ? "#0f5132" : "#842029", fontSize: "14px", fontWeight: "600", boxShadow: "0 4px 12px #0002",
  });
  document.body.appendChild(node);
  window.setTimeout(() => node.remove(), 3000);
};
const toApiDate = (value) => {
  if (!value) return "";
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return text;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};
const getGenderId = (value) => {
  if ([0, 1, 2, "0", "1", "2"].includes(value)) return Number(value);
  if (String(value).toLowerCase() === "female") return 1;
  if (String(value).toLowerCase() === "other") return 2;
  return 0;
};
const getStatus = (value) => {
  const status = String(value ?? "").toLowerCase();
  return status === "1" || status === "active" ? "active" : status === "2" || status === "completed" ? "completed" : "upcoming";
};

// The same BookMaster mapping used in StudentForm.jsx:
// match competitiveExams by exam NAME, then use teaBooAssDetlst[].id (NOT BookMaster.id).
// Each exam must have two valid, distinct book-detail IDs.
async function loadBooksForExam(examName) {
  const matches = [];
  const seen = new Set();
  for (let page = 1; page <= 100; page++) {
    const response = await apiService.post("BookMaster/getPaginatedBookMasters", {
      Page: page, PageSize: 100, SearchTerm: "", SortDirection: "DESC", SortField: "Id",
      IncludeInActive: false, IsActive: true,
    });
    ensureSuccess(response.data, "Unable to load Book Master");
    const output = getOutput(response.data);
    const rows = field(output, "result", "Result") ?? [];
    if (!Array.isArray(rows)) throw new Error("Invalid Book Master response");

    for (const row of rows) {
      if (field(row, "isActive", "IsActive") === false) continue;
      const registeredNames = String(field(row, "competitiveExams", "CompetitiveExams") ?? "")
        .split(/[,;|]/).map(normalizeName).filter(Boolean);
      if (!registeredNames.includes(normalizeName(examName))) continue;

      let details = field(row, "teaBooAssDetlst", "TeaBooAssDetlst");
      if (!Array.isArray(details) || details.length === 0) {
        const masterId = number(field(row, "id", "Id"));
        if (masterId <= 0) continue;
        const fullResponse = await apiService.get(`BookMaster/getBookMasterById/${masterId}`);
        ensureSuccess(fullResponse.data, "Unable to load book details");
        const outputRow = getOutput(fullResponse.data);
        const fullMaster = field(outputRow, "bookMaster", "BookMaster") ?? outputRow;
        details = field(fullMaster, "teaBooAssDetlst", "TeaBooAssDetlst");
      }
      if (!Array.isArray(details)) continue;
      for (const book of details) {
        if (field(book, "isActive", "IsActive") === false) continue;
        const bookDetailId = number(field(book, "id", "Id"));
        const bookName = String(field(book, "bookName", "BookName") ?? "").trim();
        if (bookDetailId <= 0 || !bookName || seen.has(bookDetailId)) continue;
        seen.add(bookDetailId);
        matches.push({ bookDetailId, bookName });
      }
    }
    if (rows.length < 100) return matches.slice(0, 2);
  }
  throw new Error("Too many Book Master pages; contact administrator");
}

export default function ExamsPage({ exams, setExams, teachers, centers }) {
  const [sub, setSub] = useState("list");
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [q, setQ] = useState("");
  const [ef, setEf] = useState("All");
  const [stf, setStf] = useState("All");
  const [customCats, setCustomCats] = useState([]);
  const [customTypes, setCustomTypes] = useState([]);
  const [apiExams, setApiExams] = useState([]);
  const [examLoading, setExamLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [selectedExamIds, setSelectedExamIds] = useState([]);
  const [studentExamIds, setStudentExamIds] = useState([]);
  const [studentExamLoading, setStudentExamLoading] = useState(true);
  const [addingExams, setAddingExams] = useState(false);
  const [registerExam, setRegisterExam] = useState(null);
  const [deliveryChoice, setDeliveryChoice] = useState("");
  const [registrationBooks, setRegistrationBooks] = useState([]);
  const [booksLoading, setBooksLoading] = useState(false);
  const [booksError, setBooksError] = useState("");
  const [paymentExam, setPaymentExam] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [registeredMyExams, setRegisteredMyExams] = useState([]);
  const [registrationLookupFailed, setRegistrationLookupFailed] = useState(false);

  let storedUser = {};
  try { storedUser = JSON.parse(sessionStorage.getItem("user") || "{}"); } catch { storedUser = {}; }
  const currentTeacherId = number(sessionStorage.getItem("teacherId") ?? field(storedUser, "teacherId", "TeacherId"));
  const currentStudentId = number(sessionStorage.getItem("studentId") ?? field(storedUser, "studentId", "StudentId"));
  const isTeacher = currentTeacherId > 0;
  const isStudent = currentStudentId > 0;
  const canManageExam = !isTeacher && !isStudent;
  const allCats = [...EXAM_CATS, ...customCats];
  const allTypes = [...EXAM_TYPES, ...customTypes];

  const fetchCurrentStudent = async () => {
    if (!currentStudentId) throw new Error("Student ID not found");
    const response = await apiService.get(`Student/getStudentById/${currentStudentId}`);
    ensureSuccess(response.data, "Unable to load student details");
    const output = getOutput(response.data);
    const student = Array.isArray(output) ? output[0] : output;
    if (!student) throw new Error("Student details not found");
    return student;
  };
  const refreshStudentExamIds = async () => {
    if (!isStudent) {
      setStudentExamIds([]); setRegisteredMyExams([]); setStudentExamLoading(false);
      return;
    }
    setStudentExamLoading(true);
    setRegistrationLookupFailed(false);
    try {
      const response = await apiService.get(`Exam/getStudentIdWiseExamList?Id=${encodeURIComponent(currentStudentId)}`);
      ensureSuccess(response.data, "Unable to check registrations");
      const rows = getList(response.data);
      if (!Array.isArray(rows)) throw new Error("Unexpected My Exams response");
      setRegisteredMyExams(rows);
      setStudentExamIds([...new Set(rows.map(row => number(field(row, "examId", "ExamId", "examID", "ExamID"))).filter(id => id > 0))]);
    } catch (error) {
      console.error("Registration lookup failed", error);
      setRegistrationLookupFailed(true);
      setRegisteredMyExams([]);
      setStudentExamIds([]);
      showNotification("error", "Could not check existing registrations. Please retry.");
    } finally { setStudentExamLoading(false); }
  };
  const isExamRegistered = (exam) => {
    const id = number(field(exam, "id", "examId", "ExamId"));
    if (id > 0 && studentExamIds.includes(id)) return true;
    const name = normalizeName(field(exam, "name", "examName", "ExamName"));
    if (!name) return false;
    const candidates = registeredMyExams.filter(row => normalizeName(field(row, "examName", "ExamName")) === name);
    if (!candidates.length) return false;
    const date = toApiDate(field(exam, "date", "examDate", "ExamDate"));
    if (date && candidates.some(row => toApiDate(field(row, "examDate", "ExamDate")) === date)) return true;
    const namedExams = apiExams.filter(row => normalizeName(row.name) === name);
    return namedExams.length === 1 && candidates.length === 1 &&
      (!date || !toApiDate(field(candidates[0], "examDate", "ExamDate")));
  };

  const fetchCategoryById = async (id) => {
    if (!id) return "";
    try {
      const response = await apiService.get(`ExamCategories/getExamCategoriesById/${id}`);
      ensureSuccess(response.data, "Unable to load category");
      const root = getOutput(response.data);
      const item = Array.isArray(root) ? root[0] : field(root, "result", "Result") ?? root;
      const result = Array.isArray(item) ? item[0] : item;
      return String(field(result, "categoryName", "CategoryName", "name", "Name") ?? "");
    } catch (error) { console.error("Get Category By Id API Error", error); return ""; }
  };
  const fetchExams = async () => {
    try {
      setExamLoading(true);
      const student = isStudent ? await fetchCurrentStudent() : null;
      const studentStdId = isStudent ? number(field(student, "stdId", "StdId", "standardId", "StandardId")) : 0;
      if (isStudent && !studentStdId) throw new Error("Student standard (StdId) is missing. Please update student details.");
      const detailMediumId = number(
        field(
          getDetails(student).find(row =>
            number(field(row, "examTypeId", "ExamTypeId")) > 0
          ),
          "examTypeId",
          "ExamTypeId"
        )
      );

      const masterMediumId = number(
        field(student, "examTypeId", "ExamTypeId")
      );

      const studentMediumId = isStudent
        ? (masterMediumId > 0 ? masterMediumId : detailMediumId)
        : 0;
      if (isStudent && !studentMediumId) throw new Error("Student medium is missing. Please update student details.");
      const payload = { page: isStudent ? 1 : currentPage, pageSize: isStudent ? 100 : pageSize, searchTerm: "", sortDirection: "DESC", sortField: "Id" };
      const response = await apiService.post("Exam/getPaginatedExam", payload);
      ensureSuccess(response.data, "Failed to load exams");
      const output = getOutput(response.data);
      const rows = Array.isArray(output) ? [...output] : [...getList(response.data)];
      const count = number(field(output, "rowCount", "RowCount", "totalRecords", "TotalRecords"), rows.length);
      if (isStudent) {
        const pages = Math.ceil(Math.max(count, rows.length) / payload.pageSize);
        for (let page = 2; page <= pages; page++) {
          const extra = await apiService.post("Exam/getPaginatedExam", { ...payload, page });
          ensureSuccess(extra.data, "Unable to load all exams");
          const more = getList(extra.data);
          if (!Array.isArray(more) || !more.length) break;
          rows.push(...more);
        }
      }
      const matching = isStudent ? rows.filter(row =>
        number(field(row, "stdId", "StdId", "standardId", "StandardId")) === studentStdId &&
        number(field(row, "examTypeId", "ExamTypeId")) === studentMediumId
      ) : rows;
      const loadMaster = async (url) => {
        try {
          const response = await apiService.get(url);
          ensureSuccess(response.data, "Master lookup failed");
          return getList(response.data);
        } catch (error) { console.warn("Master lookup failed", url, error); return []; }
      };
      const [standardList, mediumList] = await Promise.all([
        loadMaster("StandardMaster/getAllStandardMasters?includeInActive=true"),
        loadMaster("ExamTypes/getAllExamTypes"),
      ]);
      const formatted = await Promise.all(matching.map(async row => {
        const categoryId = number(field(row, "categoryId", "CategoryId"));
        const categoryName = await fetchCategoryById(categoryId);
        const standardId = field(row, "standardId", "StandardId", "stdId", "StdId");
        const examTypeId = field(row, "examTypeId", "ExamTypeId");
        const standard = standardList.find(s => String(field(s, "standardId", "StandardId", "standardMasterId", "StandardMasterId", "id", "Id")) === String(standardId));
        const medium = mediumList.find(m => String(field(m, "id", "Id", "examTypeId", "ExamTypeId")) === String(examTypeId));
        return {
          ...row,
          id: field(row, "id", "Id", "examId", "ExamId"),
          name: field(row, "examName", "ExamName") ?? "",
          categoryId, categoryName, exam: categoryName || categoryId || "",
          type: field(row, "examTypeName", "ExamTypeName", "type", "examTypeId", "ExamTypeId") ?? "",
          standardName: field(standard, "standardName", "StandardName", "name", "Name") ?? field(row, "standardName", "StandardName") ?? standardId ?? "—",
          mediumName: field(medium, "typeName", "TypeName") ?? field(row, "examTypeName", "ExamTypeName") ?? "—",
          std: standardId ?? "", date: field(row, "examDate", "ExamDate") ?? "",
          startTime: field(row, "startTime", "StartTime") ?? "",
          endTime: field(row, "endTime", "EndTime") ?? "",
          duration: field(row, "durationMinutes", "DurationMinutes") ?? "",
          teacher: field(row, "teacherName", "TeacherName", "teacherId", "TeacherId") ?? "",
          center: field(row, "centerName", "CenterName", "centerId", "CenterId") ?? "",
          maxStudents: field(row, "maxStudents", "MaxStudents") ?? 0,
          students: field(row, "students", "Students", "studentCount", "StudentCount") ?? 0,
          status: getStatus(field(row, "examStatus", "ExamStatus") ?? "0"),
          resultDate: field(row, "resultDate", "ResultDate") ?? "",
          resultLink: field(row, "resultLink", "ResultLink") ?? "",
          imageUrl: field(row, "imageUrl", "ImageUrl", "examImage", "ExamImage") ?? "",
          description: field(row, "description", "Description") ?? "",
          fees: field(row, "examFee", "ExamFee", "examTotalFees", "ExamTotalFees") ?? 0,
        };
      }));
      setApiExams(formatted);
      setTotalRecords(isStudent ? formatted.length : count || formatted.length);
    } catch (error) {
      console.error("Get Exam API Error", error, error?.response?.data);
      setApiExams([]); setTotalRecords(0);
      showNotification("error", getApiErrorMessage(error, "Failed to load exams"));
    } finally { setExamLoading(false); }
  };
  useEffect(() => { fetchExams(); }, [currentPage, pageSize]);
  useEffect(() => { if (isStudent) refreshStudentExamIds(); }, [isStudent, currentStudentId]);

  // Preload exactly the books that will be saved; show them before confirmation.
  useEffect(() => {
    let cancelled = false;
    if (!registerExam) { setRegistrationBooks([]); setBooksError(""); setBooksLoading(false); return; }
    setRegistrationBooks([]);
    setBooksError("");
    setBooksLoading(true);
    loadBooksForExam(registerExam.name).then(books => {
      if (!cancelled) {
        setRegistrationBooks(books);
        if (books.length < 2) setBooksError("या परीक्षेसाठी Book Master मध्ये दोन वैध पुस्तके उपलब्ध नाहीत.");
      }
    }).catch(error => { if (!cancelled) setBooksError(getApiErrorMessage(error, "Books load failed")); })
      .finally(() => { if (!cancelled) setBooksLoading(false); });
    return () => { cancelled = true; };
  }, [registerExam?.id, registerExam?.name]);

  const filtered = useMemo(() => apiExams.filter(e => {
    const dateLabel = e.date ? new Date(e.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";
    const searchable = [e.name, e.exam, e.date, dateLabel, e.students, e.status, e.teacher, e.center, e.fees].join(" ").toLowerCase();
    return searchable.includes(q.toLowerCase()) && (ef === "All" || e.exam === ef) && (stf === "All" || e.status === stf.toLowerCase());
  }), [apiExams, q, ef, stf]);
  const totalPages = Math.max(1, Math.ceil((isStudent ? filtered.length : totalRecords) / pageSize));
  const pageRecords = isStudent ? filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize) : filtered.slice(0, pageSize);

  // Update Student: preserve existing exam/payment rows; create one detail per NEW exam.
  // Book dispatch rows are inserted separately, one row per BookDetailId.
  const handleAddSelectedExams = async (examIds = selectedExamIds, selectedDelivery = "shop") => {
    if (!isStudent || registrationLookupFailed || addingExams) return;
    if (!currentStudentId) return showNotification("error", "Student ID not found");
    if (!examIds.length) return showNotification("error", "Please select an exam");
    if (!["shop", "courier"].includes(selectedDelivery)) return showNotification("error", "कृपया पुस्तके घेण्याची पद्धत निवडा.");
    try {
      setAddingExams(true);
      const student = await fetchCurrentStudent();
      const studentId = number(field(student, "id", "Id"), currentStudentId);
      const existingDetails = getDetails(student);
      const normalizedExisting = existingDetails.map(detail => {
        const examTotalFees = number(field(detail, "examTotalFees", "ExamTotalFees"));
        const feesPaid = number(field(detail, "feesPaid", "FeesPaid"));
        const pendingRaw = field(detail, "pendingFees", "PendingFees");
        const courierCharges = number(
          field(detail, "courierCharges", "CourierCharges")
        );

        const pendingFees =
          pendingRaw !== undefined &&
            pendingRaw !== null &&
            pendingRaw !== ""
            ? number(pendingRaw)
            : Math.max(
              0,
              examTotalFees + courierCharges - feesPaid
            ); return {
              Id: number(field(detail, "id", "Id", "studentDetailId", "StudentDetailId")),
              StudentId: studentId,
              ExamId: number(field(detail, "examId", "ExamId")),
              ExamTypeId: number(field(detail, "examTypeId", "ExamTypeId")),
              BookDetailId: String(field(detail, "bookDetailId", "BookDetailId") ?? ""),
              PostTrackNo: field(detail, "postTrackNo", "PostTrackNo") ?? null,
              PostTrackDate: field(detail, "postTrackDate", "PostTrackDate") ?? null,
              CourierCharges: number(field(detail, "courierCharges", "CourierCharges")),
              IsBookSend: asBool(field(detail, "isBookSend", "IsBookSend")),
              ExamTotalFees: examTotalFees, FeesPaid: feesPaid, PendingFees: pendingFees,
              IsPaid: asBool(field(detail, "isPaid", "IsPaid") ?? (pendingFees <= 0 && examTotalFees > 0)),
              IsActive: asBool(field(detail, "isActive", "IsActive") ?? true),
            };
      }).filter(detail => detail.ExamId > 0);

      // Do not allow an already registered exam to be appended again.
      const alreadyRegistered = new Set([...studentExamIds, ...normalizedExisting.map(d => d.ExamId)]);
      const newExamIds = [...new Set(examIds.map(Number))].filter(id => id > 0 && !alreadyRegistered.has(id));
      if (!newExamIds.length) {
        showNotification("error", "Selected exams are already added to My Exams.");
        await refreshStudentExamIds();
        return;
      }
      const detailExamTypeId = number(
        field(
          existingDetails.find(d =>
            number(field(d, "examTypeId", "ExamTypeId")) > 0
          ),
          "examTypeId",
          "ExamTypeId"
        )
      );

      const masterExamTypeId = number(
        field(student, "examTypeId", "ExamTypeId")
      );

      const studentExamTypeId =
        masterExamTypeId > 0
          ? masterExamTypeId
          : detailExamTypeId;

      if (studentExamTypeId <= 0) {
        throw new Error("Student ExamTypeId is missing.");
      }

      const newDetails = [];
      const newBookSends = [];
      for (const examId of newExamIds) {
        const exam = apiExams.find(item => number(item.id) === examId);
        if (!exam) throw new Error(`Exam ${examId} not found.`);
        const examFee = Number(exam.fees);
        if (!Number.isFinite(examFee) || examFee < 0) throw new Error(`Invalid exam fee: ${exam.name}`);

        // Use exactly the book-detail IDs displayed in the registration popup.
        const books = registerExam && number(registerExam.id) === examId && registrationBooks.length === 2
          ? registrationBooks : await loadBooksForExam(exam.name);
        const bookIds = [...new Set(books.map(book => number(book.bookDetailId)))];
        if (bookIds.length !== 2 || bookIds.some(id => !Number.isInteger(id) || id <= 0)) {
          throw new Error(`${exam.name}: two valid BookDetailIds are required. Check Book Master.`);
        }

        const courierCharge = selectedDelivery === "courier" ? COURIER_FEE_PER_EXAM : 0;
        newDetails.push({
          Id: 0,
          StudentId: studentId,
          ExamId: examId,
          ExamTypeId: studentExamTypeId,
          BookDetailId: bookIds.join(","),
          PostTrackNo: "0",
          PostTrackDate: null,
          CourierCharges: courierCharge,
          IsBookSend: false,
          ExamTotalFees: examFee,
          FeesPaid: 0,
          PendingFees: examFee + courierCharge,
          IsPaid: false,
          IsActive: true,
        });

        // BookSend stores INT book IDs, so each book gets its own row.
        bookIds.forEach((bookId, index) => newBookSends.push({
          StudentId: studentId,
          ExamId: examId,
          BookDetailId: bookId,
          PostTrackNo: "0",
          PostTrackDate: null,
          CourierCharges: index === 0 ? courierCharge : 0,
          IsBookSend: false,
          IsActive: true,
          CreatedBy: number(field(storedUser, "userId", "UserId", "id", "Id") ?? sessionStorage.getItem("userId"), 0),
        }));
      }
      const updateRequest = {
        Id: studentId,
        DocumentNo: field(student, "documentNo", "DocumentNo", "roll") ?? "",
        FirstName: field(student, "firstName", "FirstName") ?? "",
        MiddleName: field(student, "middleName", "MiddleName") ?? "",
        LastName: field(student, "lastName", "LastName") ?? "",
        Gender: getGenderId(field(student, "gender", "Gender")),
        DOB: toApiDate(field(student, "dob", "DOB", "dateOfBirth", "DateOfBirth")),
        FatherName: field(student, "fatherName", "FatherName", "parentName") ?? "",
        FatherPhone: field(student, "fatherPhone", "FatherPhone", "parentMobile") ?? "",
        FatherEmail: field(student, "fatherEmail", "FatherEmail", "parentEmail") ?? "",
        Address: field(student, "address", "Address") ?? "",
        Pincode: String(field(student, "pincode", "Pincode") ?? ""),
        StateId: number(field(student, "stateId", "StateId"), 1),
        DistrictId: number(field(student, "districtId", "DistrictId")),
        TalukaId: number(field(student, "talukaId", "TalukaId")),
        SchoolId: number(field(student, "schoolId", "SchoolId")),
        StdId: number(field(student, "stdId", "StdId", "standardId", "StandardId")),
        StdStatusId: number(field(student, "stdStatusId", "StdStatusId")),
        ExamTypeId: studentExamTypeId,
        IsActive: 1,
        DeliveryMethod: selectedDelivery,
        CourierCharges: selectedDelivery === "courier" ? COURIER_FEE_PER_EXAM * newExamIds.length : 0,
        StudentDetails: [...normalizedExisting, ...newDetails],
      };
      console.info("STUDENT ADD EXAMS REQUEST", updateRequest);
      const response = await apiService.put("Student/updateStudent", updateRequest);
      ensureSuccess(response.data, "Unable to add selected exams.");

      // Student/updateStudent has now saved the new exam detail row.
      // Do not retry Student/updateStudent if a subsequent BookSend insert fails.
      for (const bookSend of newBookSends) {
        try {
          const insertResponse = await apiService.post("StudentBookSend/addStudentBookSend", bookSend);
          ensureSuccess(insertResponse.data, `Unable to save BookDetailId ${bookSend.BookDetailId}`);
        } catch (bookError) {
          throw new Error(
            `Exam registration saved, but BookSend entry for book ${bookSend.BookDetailId} failed: ` +
            `${getApiErrorMessage(bookError, "BookSend insert failed")}. ` +
            `Do not register this exam again; check existing BookSend rows before retrying.`
          );
        }
      }

      setSelectedExamIds([]); setRegisterExam(null); setDeliveryChoice("");
      await refreshStudentExamIds();
      showNotification("success", `${newExamIds.length} exam(s) registered successfully!`);

      // Fetch the new exam detail row for payment.
      try {
        const refreshed = await fetchCurrentStudent();
        const newlyRegisteredId = newExamIds[0];
        const feeRow = getDetails(refreshed).find(detail =>
          number(field(detail, "examId", "ExamId")) === newlyRegisteredId &&
          asBool(field(detail, "isActive", "IsActive") ?? true) &&
          number(field(detail, "pendingFees", "PendingFees")) > 0
        );
        const detailId = number(field(feeRow, "id", "Id", "studentDetailId", "StudentDetailId"));
        const selectedExam = apiExams.find(item => number(item.id) === newlyRegisteredId);
        if (detailId > 0) {
          setPaymentExam({
            studentDetailId: detailId, studentId,
            examName: selectedExam?.name ?? "Exam", examDate: selectedExam?.date,
            examTotalFees: number(field(feeRow, "examTotalFees", "ExamTotalFees")),
            courierCharges: number(field(feeRow, "courierCharges", "CourierCharges")),
            pendingFees: number(field(feeRow, "pendingFees", "PendingFees")),
          });
        } else showNotification("error", "Registration saved. Payment detail not found; open My Exams to pay.");
      } catch (error) {
        console.warn("Registered but payment details could not load", error);
        showNotification("error", "Registration saved. Open My Exams to pay.");
      }
    } catch (error) {
      console.error("Add Exams Error", error);
      showNotification("error", getApiErrorMessage(error, "Unable to add selected exams"));
    } finally { setAddingExams(false); }
  };

  // Razorpay amount is always taken from the backend order, never from the displayed fee.
  const payRegisteredExam = async () => {
    if (!paymentExam?.studentDetailId || paymentLoading) return;
    try {
      setPaymentLoading(true);
      const studentId = number(paymentExam.studentId ?? currentStudentId);
      const studentDetailId = number(paymentExam.studentDetailId);
      const response = await apiService.post("Payment/createRazorpayOrder", { studentId, studentDetailIds: [studentDetailId] });
      ensureSuccess(response.data, "Cannot create payment order");
      const order = getOutput(response.data);
      const orderId = field(order, "orderId", "OrderId", "id", "Id");
      const amount = number(field(order, "amount", "Amount"));
      const currency = field(order, "currency", "Currency") ?? "INR";
      if (!orderId || amount <= 0) throw new Error("Invalid backend payment order/amount. Please use My Exams to pay.");
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const script = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
          if (script) {
            script.addEventListener("load", resolve, { once: true });
            script.addEventListener("error", reject, { once: true });
          } else {
            const element = document.createElement("script");
            element.src = "https://checkout.razorpay.com/v1/checkout.js";
            element.async = true; element.onload = resolve; element.onerror = reject;
            document.body.appendChild(element);
          }
        });
      }
      if (!window.Razorpay) throw new Error("Razorpay could not load");
      const razorpay = new window.Razorpay({
        key: "rzp_live_TkB8Olj4FbJVxc", // Public Key ID only; never expose Razorpay Key Secret.
        amount: Math.round(amount * 100), currency, order_id: orderId,
        name: "GoreExam", description: `${paymentExam.examName} Exam Fee + Courier (if applicable)`,
        prefill: {
          name: field(storedUser, "name", "Name") ?? "",
          email: field(storedUser, "email", "Email") ?? "",
          contact: field(storedUser, "mobileNo", "MobileNo", "phone") ?? "",
        },
        notes: { studentId: String(studentId), studentDetailId: String(studentDetailId), examName: paymentExam.examName },
        handler: async (payment) => {
          try {
            const verify = await apiService.post("Payment/verifyRazorpayPayment", {
              orderId: payment.razorpay_order_id,
              razorpayPaymentId: payment.razorpay_payment_id,
              razorpaySignature: payment.razorpay_signature,
            });
            ensureSuccess(verify.data, "Payment verification failed");
            showNotification("success", "Payment completed successfully!");
            setPaymentExam(null);
            await refreshStudentExamIds();
          } catch (error) {
            showNotification("error", getApiErrorMessage(error, "Payment verification failed. Check My Exams."));
          } finally { setPaymentLoading(false); }
        },
        modal: { ondismiss: () => setPaymentLoading(false) },
        theme: { color: "#2563eb" },
      });
      razorpay.on("payment.failed", result => {
        showNotification("error", result?.error?.description ?? "Payment failed");
        setPaymentLoading(false);
      });
      razorpay.open();
    } catch (error) {
      showNotification("error", getApiErrorMessage(error, "Unable to start payment"));
      setPaymentLoading(false);
    }
  };

  const openExamForEdit = async (exam) => {
    try {
      const examId = field(exam, "id", "Id", "examId", "ExamId");
      if (!examId) throw new Error("Exam ID not found");
      setExamLoading(true);
      const response = await apiService.get(`Exam/getExamById/${examId}`);
      ensureSuccess(response.data, "Failed to load exam");
      const output = getOutput(response.data);
      const item = Array.isArray(output) ? output[0] : field(output, "result", "Result") ?? output;
      if (!item) throw new Error("Exam details not found");
      setEdit({
        ...exam, ...item, id: field(item, "id", "Id") ?? examId,
        resultDate: field(item, "resultDate", "ResultDate") ?? "",
        resultLink: field(item, "resultLink", "ResultLink") ?? "",
        imageUrl: field(item, "imageUrl", "ImageUrl", "examImage", "ExamImage") ?? "",
        centerId: field(item, "centerId", "CenterId") ?? field(exam, "centerId") ?? "",
        categoryId: field(item, "categoryId", "CategoryId") ?? field(exam, "categoryId") ?? "",
        standardId: field(item, "standardId", "StandardId") ?? field(exam, "standardId") ?? "",
        examTypeId: field(item, "examTypeId", "ExamTypeId") ?? field(exam, "examTypeId") ?? "",
      });
      setSub("edit");
    } catch (error) { showNotification("error", getApiErrorMessage(error, "Failed to load exam details")); }
    finally { setExamLoading(false); }
  };
  const doDelete = async () => {
    if (!del?.id) return;
    try {
      const response = await apiService.delete(`Exam/deleteExam/${del.id}`);
      ensureSuccess(response.data, "Failed to delete exam");
      showNotification("success", field(response.data, "message", "Message") ?? "Exam deleted successfully!");
      setDel(null); await fetchExams();
    } catch (error) { showNotification("error", getApiErrorMessage(error, "Failed to delete exam")); }
  };
  const doSave = async data => {
    if (edit) {
      setApiExams(previous => previous.map(row => row.id === edit.id ? { ...row, ...data } : row));
      showNotification("success", "Exam updated successfully!");
    } else {
      setApiExams(previous => [...previous, { ...data, id: Date.now(), students: 0 }]);
      showNotification("success", "Exam created successfully!");
    }
    setSub("list"); setEdit(null);
    if (currentPage !== 1) setCurrentPage(1);
    else await fetchExams();
  };
  if (sub === "create") return <ExamForm title="Create Exam" teachers={teachers} centers={centers}
    allCats={allCats} allTypes={allTypes} setCustomCats={setCustomCats} setCustomTypes={setCustomTypes}
    onSave={doSave} onBack={() => setSub("list")} />;
  if (sub === "edit" && edit) return <ExamForm title="Edit Exam" initial={edit} teachers={teachers} centers={centers}
    allCats={allCats} allTypes={allTypes} setCustomCats={setCustomCats} setCustomTypes={setCustomTypes}
    onSave={doSave} onBack={() => { setSub("list"); setEdit(null); }} />;
  if (sub === "view" && view) return <ExamDetailView exam={view}
    onBack={() => { setSub("list"); setView(null); }}
    onEdit={canManageExam ? e => { setEdit(e); setSub("edit"); } : undefined} />;

  const dateText = value => value ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
  const closeRegister = () => { setRegisterExam(null); setDeliveryChoice(""); };
  const registrationTotal = number(registerExam?.fees) + (deliveryChoice === "courier" ? COURIER_FEE_PER_EXAM : 0);
  return <>
    <style>{`
      .exams-pagination-footer{box-sizing:border-box;display:flex!important;align-items:center!important;justify-content:space-between!important;flex-direction:row!important;flex-wrap:nowrap!important;gap:8px!important;width:100%!important;max-width:100%!important;min-width:0!important;overflow:hidden!important}
      .exams-pagination-count{flex:0 0 auto!important;white-space:nowrap!important}
      .exams-pagination-controls{display:flex!important;align-items:center!important;justify-content:flex-end!important;flex:1 1 0!important;gap:5px!important;min-width:0!important;flex-wrap:nowrap!important;overflow:hidden!important}
      .exams-pagination-controls>button{flex:0 0 auto!important;white-space:nowrap!important}
      .exams-pagination-numbers{display:flex!important;flex:0 1 auto!important;align-items:center!important;gap:4px!important;min-width:0!important;max-width:100%!important;overflow-x:auto!important;overflow-y:hidden!important;flex-wrap:nowrap!important;white-space:nowrap!important;scrollbar-width:thin}
      .exams-pagination-numbers>button{flex:0 0 auto!important}
      @media(max-width:600px){.exams-pagination-footer{gap:5px!important;padding:10px 6px!important}.exams-pagination-count{font-size:9px!important}.exams-pagination-controls{gap:3px!important}.exams-pagination-controls>button{min-width:43px!important;padding:0 4px!important;font-size:9px!important}.exams-pagination-numbers{flex:1 1 0!important;gap:3px!important}.exams-pagination-numbers>button{min-width:28px!important;padding:0 5px!important}}
    `}</style>
    <div className="ph"><div className="ph-t">{isStudent ? "Exams" : "Exams Management"}</div></div>
    <div className="toolbar">
      <SB value={q} onChange={value => { setQ(value); if (currentPage !== 1) setCurrentPage(1); }} placeholder={isStudent ? "Search exams..." : "Search exams, teacher…"} />
      {canManageExam && <button className="btn btn-p" onClick={() => setSub("create")}><I n="plus" s={13} c="#fff" /> Create Exam</button>}
    </div>
    <div className="card">
      <div className="tw" style={{ overflowX: "auto", width: "100%" }}>
        <table style={{ minWidth: 1080, width: "100%" }}>
          <thead><tr><th>Exam Name</th><th>Std</th><th>Medium</th><th>Date</th><th style={{ minWidth: 100, textAlign: "right" }}>Fee</th>
            {isStudent && <th style={{ minWidth: 145, textAlign: "center" }}>Registration</th>}
            <th style={{ minWidth: 120 }}>Result Date</th><th style={{ minWidth: 120 }}>Result Link</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {examLoading ? <tr><td colSpan={isStudent ? 10 : 9} style={{ textAlign: "center", padding: 28, color: "var(--t3)" }}>Loading exams...</td></tr> :
              filtered.length === 0 ? <tr><td colSpan={isStudent ? 10 : 9} style={{ textAlign: "center", padding: 28, color: "var(--t3)" }}>📝 No exams found</td></tr> :
                pageRecords.map(e => <tr key={e.id} style={{ cursor: "pointer" }} onClick={() => { setView(e); setSub("view"); }}>
                  <td className="tdb">{e.name}</td><td>{e.standardName ?? e.std ?? "—"}</td><td>{e.mediumName || "—"}</td><td className="td2">{dateText(e.date)}</td>
                  <td className="td2" style={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: 700 }}>{Number.isFinite(Number(e.fees)) ? money(e.fees) : "-"}</td>
                  {isStudent && <td onClick={event => event.stopPropagation()} style={{ textAlign: "center" }}>
                    {studentExamLoading ? <span style={{ color: "#64748b", fontSize: 12 }}>Checking...</span> : isExamRegistered(e) ?
                      <span style={{ display: "inline-block", padding: "7px 12px", borderRadius: 20, background: "#dcfce7", color: "#166534", fontWeight: 700, fontSize: 12 }}>Registered</span> :
                      <button type="button" className="btn btn-p btn-sm" disabled={addingExams || paymentLoading || registrationLookupFailed}
                        onClick={() => { if (!registrationLookupFailed) { setDeliveryChoice(""); setRegisterExam(e); } }}
                        style={{ whiteSpace: "nowrap", borderRadius: 8, padding: "8px 14px", background: "#2563eb", color: "#fff" }}>Register Now</button>}</td>}
                  <td className="td2" style={{ whiteSpace: "nowrap" }}>{dateText(e.resultDate)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{e.resultLink ? <a href={e.resultLink} target="_blank" rel="noreferrer" className="btn btn-p btn-sm" onClick={event => event.stopPropagation()} style={{ textDecoration: "none", display: "inline-flex" }}>View Result</a> : "-"}</td>
                  <td><Bdg type={e.status === "completed" ? "green" : e.status === "active" ? "blue" : "yellow"} label={e.status ? e.status.charAt(0).toUpperCase() + e.status.slice(1) : ""} /></td>
                  <td onClick={event => event.stopPropagation()}><div className="ra">
                    <button className="btn btn-p btn-sm" title="View" onClick={() => { setView(e); setSub("view"); }}><I n="eye" s={11} c="#fff" /></button>
                    {canManageExam && <><button className="btn btn-g btn-sm" title="Edit" onClick={() => openExamForEdit(e)}><I n="edit" s={11} /></button>
                      <button className="btn btn-d btn-sm" title="Delete" onClick={() => setDel(e)}><I n="trash" s={11} /></button></>}
                  </div></td>
                </tr>)}
          </tbody>
        </table>
      </div>
      {!examLoading && (isStudent ? filtered.length : totalRecords) > 0 &&
        <div className="exams-pagination-footer" style={{ padding: "14px 16px", borderTop: "1px solid var(--bd)" }}>
          <div className="exams-pagination-count" style={{ display: "flex", fontSize: 11, color: "var(--t3)" }}>
            Showing <strong style={{ margin: "0 4px" }}>{(currentPage - 1) * pageSize + 1}</strong>–<strong style={{ margin: "0 4px" }}>{Math.min(currentPage * pageSize, isStudent ? filtered.length : totalRecords)}</strong> of <strong style={{ marginLeft: 4 }}>{isStudent ? filtered.length : totalRecords}</strong>
          </div>
          <div className="exams-pagination-controls">
            <button type="button" disabled={currentPage === 1 || examLoading} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} style={{ height: 32, minWidth: 66, border: "1px solid #e2e8f0", borderRadius: 6, background: "white", color: "#475569" }}>‹ Back</button>
            <div className="exams-pagination-numbers">{Array.from({ length: totalPages }, (_, i) => i + 1).map(page =>
              <button key={page} type="button" disabled={examLoading} onClick={() => setCurrentPage(page)} style={{ height: 32, minWidth: 32, borderRadius: 6, border: page === currentPage ? "1px solid #2563eb" : "1px solid #e2e8f0", background: page === currentPage ? "#2563eb" : "white", color: page === currentPage ? "white" : "#475569" }}>{page}</button>
            )}</div>
            <button type="button" disabled={currentPage >= totalPages || examLoading} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} style={{ height: 32, minWidth: 66, border: "1px solid #e2e8f0", borderRadius: 6, background: "white", color: "#475569" }}>Next ›</button>
          </div>
        </div>}
    </div>
    {del && <ConfirmDlg title="Delete Exam" msg={`Delete "${del.name}"?`} onOk={doDelete} onCancel={() => setDel(null)} />}

    {isStudent && registerExam && typeof document !== "undefined" && createPortal(
      <div role="presentation" onMouseDown={e => { if (e.target === e.currentTarget && !addingExams) closeRegister(); }}
        style={{ position: "fixed", inset: 0, zIndex: 999999, background: "rgba(15,23,42,.60)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12, overflowY: "auto" }}>
        <div role="dialog" aria-modal="true" aria-label="Books & Courier registration"
          style={{ background: "#fff", width: "100%", maxWidth: 560, maxHeight: "calc(100dvh - 24px)", display: "flex", flexDirection: "column", borderRadius: 12, overflow: "hidden", boxShadow: "0 24px 64px #0003" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #e5e7eb", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, color: "#234C80", fontSize: 16, fontWeight: 800 }}>
              <I n="box" s={19} c="#234C80" /> Books & Courier / पुस्तके व कुरिअर
            </div>
            <button type="button" onClick={closeRegister} disabled={addingExams} aria-label="Close registration popup"
              style={{ border: 0, background: "transparent", cursor: "pointer", fontSize: 23, color: "#64748b", padding: "0 4px" }}>×</button>
          </div>
          <div style={{ padding: "18px 20px", overflowY: "auto", minHeight: 0 }}>
            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 5 }}>Selected Examination / निवडलेली परीक्षा</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: "#1e293b" }}>{registerExam.name}</div>
            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12 }}>
              निवडलेल्या परीक्षेची पहिली दोन पुस्तके आपोआप निवडली जातील.
            </div>
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 9, color: "#334155" }}>Selected Books / निवडलेली पुस्तके</div>
              {booksLoading ? <div style={{ fontSize: 11 }}>Loading books...</div> : booksError ?
                <div role="alert" style={{ fontSize: 11, color: "#b91c1c" }}>{booksError}</div> :
                registrationBooks.length === 0 ? <div style={{ fontSize: 11, color: "#92400e" }}>No books mapped to this exam in Book Master.</div> :
                registrationBooks.map(book => (
                  <label key={book.bookDetailId} style={{ display: "flex", gap: 9, alignItems: "center", padding: "6px 2px", color: "#475569", fontSize: 11.5 }}>
                    <input type="checkbox" checked disabled readOnly aria-label={`${book.bookName} automatically selected`}
                      style={{ width: 16, height: 16, accentColor: "#234C80" }} />
                    <span>{book.bookName}</span>
                  </label>
                ))}
            </div>
            <p style={{ marginTop: 12, fontSize: 11, lineHeight: 1.7, color: "#7c3d12", background: "#fff7ed", padding: "10px 12px", borderRadius: 8 }}>
              सूचना: नोंदणी केल्यानंतर पुस्तके दिली जातील. प्रश्नसंच परीक्षेच्या दोन महिने आधी दिले जातील. प्रश्नसंच पाठविण्याचे कुरिअर शुल्क स्वतंत्रपणे आकारले जाईल.
            </p>
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #eef2f7" }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10 }}>Delivery Method</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {[
                  { value: "shop", label: "Collect from Office / School", mr: "ऑफिस / शाळेतून घेणार" },
                  { value: "courier", label: "Courier Delivery", mr: "कुरिअरद्वारे पाठवा" },
                ].map(option => {
                  const active = deliveryChoice === option.value;
                  return <button type="button" key={option.value} disabled={addingExams}
                    onClick={() => setDeliveryChoice(option.value)} aria-pressed={active}
                    style={{ padding: "9px 12px", borderRadius: 7, border: active ? "1px solid #234C80" : "1px solid #dbe1e8", background: active ? "#eef4ff" : "#fff", color: active ? "#234C80" : "#475569", cursor: addingExams ? "not-allowed" : "pointer", fontSize: 10.5, fontWeight: 600, textAlign: "left" }}>
                    {option.label}
                    <span style={{ display: "block", fontSize: 9, marginTop: 2, fontWeight: 400 }}>{option.mr}</span>
                  </button>;
                })}
              </div>
            </div>
            {deliveryChoice === "courier" && <p style={{ marginTop: 12, fontSize: 12, fontWeight: 600, lineHeight: 1.7, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "10px 12px", color: "#1e3a8a" }}>
              पुस्तकांचे कुरिअर शुल्क: ₹50 प्रति परीक्षा × 1 = {money(COURIER_FEE_PER_EXAM)}. प्रश्नसंचाचे कुरिअर शुल्क स्वतंत्रपणे आकारले जाईल.
            </p>}
            <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px", marginTop: 16, fontSize: 12, color: "#334155" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}><span>Exam Fees / परीक्षा शुल्क</span><strong>{money(registerExam.fees)}</strong></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}><span>Courier Charges / कुरिअर शुल्क</span><strong>{money(deliveryChoice === "courier" ? COURIER_FEE_PER_EXAM : 0)}</strong></div>
              <div style={{ borderTop: "1px solid #cbd5e1", paddingTop: 9, display: "flex", justifyContent: "space-between", fontWeight: 800 }}><span>Total / एकूण शुल्क</span><span>{money(registrationTotal)}</span></div>
            </div>
            <p style={{ color: "#64748b", fontSize: 11, lineHeight: 1.6, marginTop: 12 }}>
              Confirm केल्यानंतर परीक्षा My Exams मध्ये नोंदवली जाईल. पुढे Payment करता येईल.
            </p>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 10, padding: "12px 18px", borderTop: "1px solid #e5e7eb", background: "#fff", flexShrink: 0 }}>
            <button type="button" className="btn btn-g" disabled={addingExams} onClick={closeRegister}>Cancel</button>
            <button type="button" className="btn btn-p"
              disabled={addingExams || booksLoading || !!booksError || registrationBooks.length !== 2 || !deliveryChoice}
              onClick={() => handleAddSelectedExams([number(registerExam.id)], deliveryChoice)}>
              {addingExams ? "Registering..." : "Confirm & Continue to Payment"}
            </button>
          </div>
        </div>
      </div>, document.body)}

    {isStudent && paymentExam && typeof document !== "undefined" && createPortal(
      <div role="presentation" onMouseDown={e => { if (e.target === e.currentTarget && !paymentLoading) setPaymentExam(null); }}
        style={{ position: "fixed", inset: 0, zIndex: 999999, background: "rgba(15,23,42,.62)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <div role="dialog" aria-modal="true" aria-label="Exam fee payment" style={{ background: "white", width: "100%", maxWidth: 450, borderRadius: 18, overflow: "hidden", boxShadow: "0 25px 70px #0004" }}>
          <div style={{ background: "linear-gradient(120deg,#1d4ed8,#4f46e5)", color: "white", padding: "22px 24px", fontSize: 19, fontWeight: 800 }}>Exam Fee Payment</div>
          <div style={{ padding: 24 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 14 }}>{paymentExam.examName}</div>
            <div style={{ fontSize: 13, color: "#475569", marginBottom: 7 }}>Exam Fees: <strong>{money(paymentExam.examTotalFees)}</strong></div>
            <div style={{ fontSize: 13, color: "#475569", marginBottom: 7 }}>Courier Charges: <strong>{money(paymentExam.courierCharges)}</strong></div>
            <div style={{ fontSize: 14, color: "#475569", marginBottom: 8 }}>Pending Fees: <strong>{money(paymentExam.pendingFees)}</strong></div>
            <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>Registration saved. Complete payment securely, or pay later from My Exams. Razorpay amount comes from the backend order.</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 22 }}>
              <button type="button" className="btn btn-g" disabled={paymentLoading} onClick={() => setPaymentExam(null)}>Pay Later</button>
              <button type="button" className="btn btn-p" disabled={paymentLoading || number(paymentExam.pendingFees) <= 0} onClick={payRegisteredExam}>{paymentLoading ? "Processing..." : "Pay Now"}</button>
            </div>
          </div>
        </div>
      </div>, document.body)}
  </>;
}
